import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Eye, FileText } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGE_METADATA, getPrivacyStructuredData } from '../seo/pageMetadata';

const PrivacyPolicy = () => {
  return (
    <>
      <Seo {...PAGE_METADATA.privacy} structuredData={getPrivacyStructuredData()} />
      <div className="pt-32 pb-24">
      <section className="px-6 max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-6">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-blue-600 text-[10px] font-black uppercase tracking-[0.2em]">Legal Information</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-display font-black text-royal-dark tracking-tighter mb-6">
            Privacy <span className="text-royal-purple">Policy</span>
          </h1>
          <p className="text-lg text-slate-500 font-medium">
            Last Updated: April 2026
          </p>
        </motion.div>

        <div className="prose prose-slate max-w-none space-y-12">
          <section>
            <h2 className="text-3xl font-display font-black text-royal-dark mb-6 flex items-center gap-3">
              <Eye className="w-8 h-8 text-royal-purple" />
              1. Information We Collect
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg font-medium">
              We collect information that you provide directly to us when you apply for our programs, sign up for our newsletter, or contact us. This may include:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-600 font-medium">
              <li>Name, email address, and phone number.</li>
              <li>Business information (name, industry, size).</li>
              <li>Application details for empowerment grants or mentorship.</li>
              <li>Any other information you choose to provide.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-display font-black text-royal-dark mb-6 flex items-center gap-3">
              <Lock className="w-8 h-8 text-royal-purple" />
              2. How We Use Your Information
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg font-medium">
              The information we collect is used to:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-600 font-medium">
              <li>Process your applications for grants and mentorship programs.</li>
              <li>Communicate with you about our initiatives and updates.</li>
              <li>Improve our platform and services.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-display font-black text-royal-dark mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8 text-royal-purple" />
              3. Data Protection
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg font-medium">
              We implement a variety of security measures to maintain the safety of your personal information. Your data is stored on secure servers and is only accessible by authorized personnel who are required to keep the information confidential.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-display font-black text-royal-dark mb-6 flex items-center gap-3">
              <FileText className="w-8 h-8 text-royal-purple" />
              4. Third-Party Disclosure
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg font-medium">
              We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties. This does not include trusted third parties who assist us in operating our website or conducting our business, so long as those parties agree to keep this information confidential.
            </p>
          </section>

          <section className="bg-slate-50 p-10 rounded-[3rem] border border-slate-100">
            <h2 className="text-2xl font-display font-black text-royal-dark mb-4">Contact Us Regarding Privacy</h2>
            <p className="text-slate-600 font-medium mb-6">
              If you have any questions regarding this privacy policy, you may contact us using the information below:
            </p>
            <div className="text-royal-purple font-black">
              Email: privacy@delightempowerment.org
            </div>
          </section>
        </div>
      </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;
